// API utilities and auth
const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Local storage keys
const TOKEN_KEY = 'i5_admin_token';
const ADMIN_KEY = 'i5_admin_data';

// Helper function using XMLHttpRequest to avoid fetch interception issues
const xhrRequest = (url, method, body, headers = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    
    // Set headers
    xhr.setRequestHeader('Content-Type', 'application/json');
    Object.keys(headers).forEach(key => {
      xhr.setRequestHeader(key, headers[key]);
    });
    
    xhr.onreadystatechange = function() {
      if (xhr.readyState === 4) {
        try {
          const data = xhr.responseText ? JSON.parse(xhr.responseText) : {};
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(data);
          } else if (xhr.status === 401) {
            reject({ status: 401, detail: data.detail || 'Unauthorized' });
          } else {
            reject({ status: xhr.status, detail: data.detail || 'Request failed' });
          }
        } catch (e) {
          reject({ status: xhr.status, detail: 'Invalid server response' });
        }
      }
    };
    
    xhr.onerror = function() {
      reject({ status: 0, detail: 'Network error' });
    };
    
    xhr.send(body ? JSON.stringify(body) : null);
  });
};

export const api = {
  // Auth helpers
  setToken: (token) => localStorage.setItem(TOKEN_KEY, token),
  getToken: () => localStorage.getItem(TOKEN_KEY),
  removeToken: () => localStorage.removeItem(TOKEN_KEY),
  setAdminData: (data) => localStorage.setItem(ADMIN_KEY, JSON.stringify(data)),
  getAdminData: () => {
    const data = localStorage.getItem(ADMIN_KEY);
    return data ? JSON.parse(data) : null;
  },
  removeAdminData: () => localStorage.removeItem(ADMIN_KEY),
  isAuthenticated: () => !!localStorage.getItem(TOKEN_KEY),
  
  // API request helper using XMLHttpRequest
  async request(endpoint, options = {}) {
    const token = this.getToken();
    const headers = {};
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    try {
      const data = await xhrRequest(
        `${API_URL}${endpoint}`,
        options.method || 'GET',
        options.body ? JSON.parse(options.body) : null,
        headers
      );
      return data;
    } catch (error) {
      if (error.status === 401) {
        this.removeToken();
        this.removeAdminData();
        if (!window.location.pathname.includes('/admin/login')) {
          window.location.href = '/admin/login';
        }
      }
      throw new Error(error.detail || 'Request failed');
    }
  },

  // Public API
  getPlans: (category) => 
    api.request(category ? `/plans?category=${category}` : '/plans'),
  
  getPlan: (planId) => 
    api.request(`/plans/${planId}`),
  
  submitLead: (leadData) => 
    api.request('/leads', {
      method: 'POST',
      body: JSON.stringify(leadData),
    }),

  // Admin Auth API
  adminLogin: (credentials) => 
    api.request('/admin/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    }),
  
  adminChangePassword: (passwordData) => 
    api.request('/admin/change-password', {
      method: 'POST',
      body: JSON.stringify(passwordData),
    }),

  // Admin Plans API
  adminGetPlans: (includeInactive = true) => 
    api.request(`/admin/plans?include_inactive=${includeInactive}`),
  
  adminCreatePlan: (planData) => 
    api.request('/admin/plans', {
      method: 'POST',
      body: JSON.stringify(planData),
    }),
  
  adminUpdatePlan: (planId, planData) => 
    api.request(`/admin/plans/${planId}`, {
      method: 'PUT',
      body: JSON.stringify(planData),
    }),
  
  adminDeletePlan: (planId) => 
    api.request(`/admin/plans/${planId}`, {
      method: 'DELETE',
    }),

  // Admin Leads API
  adminGetLeads: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.request(`/admin/leads${queryString ? `?${queryString}` : ''}`);
  },
  
  adminGetLead: (leadId) => 
    api.request(`/admin/leads/${leadId}`),
  
  adminUpdateLeadStatus: (leadId, statusData) => 
    api.request(`/admin/leads/${leadId}/status`, {
      method: 'PUT',
      body: JSON.stringify(statusData),
    }),
  
  adminExportLeads: (params = {}) => {
    const token = api.getToken();
    const queryString = new URLSearchParams(params).toString();
    
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', `${API_URL}/admin/leads/export/csv${queryString ? `?${queryString}` : ''}`, true);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.responseType = 'blob';
      
      xhr.onload = function() {
        if (xhr.status === 200) {
          resolve({ blob: () => Promise.resolve(xhr.response) });
        } else {
          reject(new Error('Export failed'));
        }
      };
      
      xhr.onerror = function() {
        reject(new Error('Network error'));
      };
      
      xhr.send();
    });
  },

  // Admin Stats API
  adminGetStats: () => 
    api.request('/admin/stats'),
};
